import { useEffect, useMemo } from 'react';
import { generatePath, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useRegisterChatPageContext } from '@trinity/chat-components/components/Chat/hooks/useRegisterPageContext.js';
import { path, querykey } from '@/consts';
import { WorkspaceBreadcrumbsContext } from '@/containers/WorkspaceObject/Context';
import { ChatContextType } from '@/enums';
import type { BrandInsightsPageContext } from '@/types';
import { FolderLoadingContext, FolderStateContext } from './Context';
import GroupFolderTagsContainer from '../GroupTags/GroupFolderTagsContainer';

type Props = {
  children: React.ReactNode;
};

export const WorkspaceFolderContainer = (props: Props) => {

  const params = useFolderParams();
  const folderId = params.folderId;
  const navigate = useNavigate();

  const apiClient = useChatApiClient();

  const query = useQuery({
    queryKey: querykey.Workspaces.Folder.Get({ folderId }),
    queryFn: () => {
      return apiClient.workspacesFolder.getFolder({
        folderId,
      });
    },
  });

  useEffect(() => {
    const e = query.error as AxiosError;
    if (query.isError && e?.status === 404) {
      navigate(path.Workspaces.FolderNotFound, { replace: true });
    }
  }, [query.isError, query.error, navigate]);

  const state = {
    capabilities: query.data?.data.capabilities,
    features: query.data?.data.features,
    meta: query.data?.data.meta,
    folder: query.data?.data.folder,
    object: query.data?.data.object,
  };

  const folderContext: BrandInsightsPageContext.Folder = useMemo(() => {
    if (!query.data?.data?.features?.insightsChat) return null;

    return {
      type: ChatContextType.Folder,
      data: {
        objectId: query.data?.data?.object?.id,
        folderId: query.data?.data?.folder?.id,
      },
      metadata: {
        supportsUploads: !!query.data?.data?.folder?.metadata?.linkedChatInstanceIdentifier,
        name: query.data?.data?.folder?.name,
      },
    };
  }, [query.data?.data?.features?.insightsChat, query.data?.data?.folder?.id, query.data?.data?.folder?.metadata?.linkedChatInstanceIdentifier, query.data?.data?.folder?.name, query.data?.data?.object?.id]);

  const register = useRegisterChatPageContext();
  useEffect(() => {
    if (!folderContext) return;

    if (folderContext.data.objectId && folderContext.metadata.name) {
      register(folderContext);
    }
  }, [register, folderContext]);

  if (query.isFetched && !query.data?.data?.folder) {
    //   const e = query.error as AxiosError<unknown>;
    const e = query.error as AxiosError<IWorkspaceObjectId | null>;
    const data = e.response.data;
    const forbidden = query.isError && e.status === 403;
    const hasObjectId = data !== null && typeof data === 'object' && !!data?.objectId;

    return forbidden && hasObjectId
      ? <Navigate to={generatePath(path.Access.Request, data)} replace />
      : <Navigate to={path.Workspaces.FolderNotFound} replace />;
  }

  return (
    <WorkspaceBreadcrumbsContext.Provider value={query.data?.data?.breadcrumbs}>
      <FolderLoadingContext.Provider value={query.isLoading}>
        <FolderStateContext.Provider value={state}>
          <GroupFolderTagsContainer folderId={params.folderId}>
            {props.children}
          </GroupFolderTagsContainer>
        </FolderStateContext.Provider>
      </FolderLoadingContext.Provider>
    </WorkspaceBreadcrumbsContext.Provider>
  );
};

type RouteParams = {
  folderId: string;
};

const useFolderParams = () => {
  const params = useParams<RouteParams>();
  return {
    folderId: +params.folderId,
  };
};